import React, { useMemo } from "react";
import { Badge, Table } from "react-bootstrap";
import moment from "moment";
import { useWebhookHistory } from "../../hooks/useWebhookHistory";
import LoadingLoader from "components/LoadingLoader";

const STATUS_VARIANT = {
  received: "secondary",
  processing: "info",
  processed: "success",
  failed: "danger",
};

// Plain-English descriptions of Stripe event types for non-technical admins.
const EVENT_LABELS = {
  "invoice.payment_succeeded": "Payment succeeded",
  "invoice.paid": "Invoice paid",
  "invoice.payment_failed": "Payment failed (card declined or error)",
  "invoice.created": "Invoice created",
  "invoice.upcoming": "Upcoming invoice notice",
  "customer.subscription.created": "Subscription started",
  "customer.subscription.updated": "Subscription updated",
  "customer.subscription.deleted": "Subscription canceled",
  "payment_intent.succeeded": "One-time charge succeeded (setup fee / add-on)",
  "payment_intent.created": "Payment started",
  "charge.succeeded": "Card charged",
  "charge.refunded": "Payment refunded",
};

const humanizeEvent = (eventType) =>
  EVENT_LABELS[eventType] ||
  eventType?.replace(/[._]/g, " ") ||
  "Unknown event";

/**
 * Renders the raw Stripe webhook event audit (WebhookEvent collection) for a
 * company: every event Stripe sent, its processing status, retry attempts and
 * last error — so an admin can confirm Stripe triggering without the dashboard.
 */
const WebhookEventsView = ({ companyId, enabled = true }) => {
  const { data, isLoading } = useWebhookHistory(companyId, 50, 0, enabled);

  const events = useMemo(() => data?.webhookEvents || [], [data]);

  if (isLoading) return <LoadingLoader />;

  if (!events.length) {
    return (
      <p className="text-muted mb-0">
        No Stripe webhook events recorded for this company.
      </p>
    );
  }

  return (
    <div>
      <h5 className="mb-3">Stripe Webhook Events</h5>
      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>#</th>
            <th>What Happened</th>
            <th>Event Type</th>
            <th>Status</th>
            <th>Amount</th>
            <th>Attempts</th>
            <th>Received</th>
            <th>Processed</th>
            <th>Last Error</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event, idx) => (
            <tr key={event.eventId || idx}>
              <td>{idx + 1}</td>
              <td>{humanizeEvent(event.eventType)}</td>
              <td>
                <code>{event.eventType}</code>
              </td>
              <td>
                <Badge bg={STATUS_VARIANT[event.status] || "secondary"}>
                  {event.status}
                </Badge>
              </td>
              <td>
                {event.amount != null
                  ? `$${Number(event.amount).toFixed(2)}`
                  : "—"}
              </td>
              <td>{event.attempts ?? "—"}</td>
              <td>
                {event.createdAt
                  ? moment(event.createdAt).format("MMM DD, YYYY h:mm A")
                  : "—"}
              </td>
              <td>
                {event.processedAt
                  ? moment(event.processedAt).format("MMM DD, YYYY h:mm A")
                  : "—"}
              </td>
              <td className="text-danger">{event.lastError || "—"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default WebhookEventsView;
